import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Main.css';
import logoWhite from '../../Assets/JMD INFORMATIQUE WHITE.png';

const services = [
  {
    icon: 'fa-solid fa-magnifying-glass',
    title: 'Diagnostic & Devis',
    price: '20,00 €',
    priceSuffix: '',
    details: 'Analyse complète de votre équipement. Devis gratuit et sans engagement.',
    highlight: false,
    modal: {
      description: 'Avant toute intervention, je réalise un diagnostic complet de votre matériel et de vos logiciels pour établir un état des lieux précis et vous proposer un devis clair.',
      includes: [
        'Analyse des performances (RAM, processeur, disque)',
        'Détection de virus, malwares et logiciels indésirables',
        'Vérification de l\'état du disque dur (risque de panne)',
        'Contrôle des mises à jour système et de sécurité',
        'Rapport verbal détaillé avec recommandations',
        'Devis gratuit et sans engagement',
      ],
      duration: '30 min à 1h',
      ideal: 'Idéal avant de décider d\'une réparation ou pour connaître l\'état réel de votre équipement.',
      note: 'Les 20 € du diagnostic sont déduits si vous acceptez l\'intervention.',
    },
  },
  {
    icon: 'fa-solid fa-screwdriver-wrench',
    title: 'Dépannage Standard',
    price: 'dès 50,00 €',
    priceSuffix: '',
    details: 'Ordinateur lent, virus, wifi, imprimantes…',
    highlight: true,
    modal: {
      description: 'Je résous les problèmes informatiques courants directement à votre domicile, sans que vous ayez à vous déplacer ou à vous séparer de votre matériel.',
      includes: [
        'Ordinateur lent : nettoyage, optimisation et accélération',
        'Suppression de virus, malwares et ransomwares',
        'Problèmes de connexion wifi ou réseau',
        'Configuration et dépannage d\'imprimante',
        'Mises à jour bloquées ou erreurs système',
        'Problèmes de démarrage, écran bleu (BSOD)',
        'Déplacement toujours inclus dans le tarif',
      ],
      duration: '1h à 2h selon le problème',
      ideal: 'Pour tous les problèmes informatiques du quotidien qui ralentissent ou empêchent l\'utilisation normale de votre PC.',
      note: null,
    },
  },
  {
    icon: 'fa-solid fa-hard-drive',
    title: 'Dépannage Avancé',
    price: 'dès 75,00 €',
    priceSuffix: '',
    details: 'Changement de disque dur, réinstallation Windows, récupération de données.',
    highlight: false,
    modal: {
      description: 'Pour les interventions techniques plus lourdes nécessitant du matériel ou une réinstallation complète du système.',
      includes: [
        'Changement de disque dur ou migration vers SSD (gain de vitesse x5)',
        'Réinstallation complète de Windows avec vos données sauvegardées',
        'Récupération de données sur disque endommagé',
        'Remplacement ou upgrade de mémoire RAM',
        'Nettoyage interne et changement de pâte thermique',
        'Configuration avancée réseau ou domaine',
        'Devis établi après diagnostic, avant intervention',
      ],
      duration: '2h à 3h (parfois sur 2 passages)',
      ideal: 'PC très lent malgré nettoyage, panne matérielle, disque dur en fin de vie ou perte de données.',
      note: 'Le prix varie selon les pièces nécessaires. Un devis vous est toujours fourni avant de commencer.',
    },
  },
  {
    icon: 'fa-solid fa-chalkboard-user',
    title: 'Initiation Informatique',
    price: '45,00 €',
    priceSuffix: '/ heure',
    details: 'Prise en main de votre ordinateur, tablette ou smartphone. À votre rythme.',
    highlight: false,
    modal: {
      description: 'Des séances personnalisées à domicile pour vous aider à être plus à l\'aise avec votre ordinateur, tablette ou smartphone — à votre propre rythme.',
      includes: [
        'Prise en main de votre ordinateur (Windows, Mac)',
        'Utilisation d\'une tablette ou d\'un smartphone',
        'Navigation sur internet en toute sécurité',
        'Gestion des emails et pièces jointes',
        'Photos, vidéos, stockage cloud (Google Drive, iCloud…)',
        'Réseaux sociaux (Facebook, WhatsApp…)',
        'Conseils pour éviter arnaques et phishing',
      ],
      duration: 'Séances d\'1 heure, plusieurs séances possibles',
      ideal: 'Débutants, seniors ou toute personne souhaitant gagner en autonomie et en confiance avec le numérique.',
      note: 'Le contenu est entièrement adapté à vos besoins. Pas de jargon technique.',
    },
  },
];

function ServiceModal({ service, onClose }) {
  const overlayRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const { modal } = service;

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="modal">
        <button
          ref={closeRef}
          className="modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        <div className="modal__header">
          <i className={`${service.icon} modal__icon`}></i>
          <div>
            <h2 className="modal__title" id="modal-title">{service.title}</h2>
            <div className="modal__price">
              <span className="modal__price-amount">{service.price}</span>
              {service.priceSuffix && (
                <span className="modal__price-suffix">{service.priceSuffix}</span>
              )}
            </div>
          </div>
        </div>

        <p className="modal__description">{modal.description}</p>

        <div className="modal__section">
          <h3 className="modal__section-title">
            <i className="fa-solid fa-circle-check"></i> Ce qui est inclus
          </h3>
          <ul className="modal__list">
            {modal.includes.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="modal__meta">
          <div className="modal__meta-item">
            <i className="fa-solid fa-clock"></i>
            <span><strong>Durée estimée :</strong> {modal.duration}</span>
          </div>
          <div className="modal__meta-item">
            <i className="fa-solid fa-user-check"></i>
            <span>{modal.ideal}</span>
          </div>
        </div>

        {modal.note && (
          <p className="modal__note">
            <i className="fa-solid fa-circle-info"></i> {modal.note}
          </p>
        )}

        <div className="modal__actions">
          <a href="tel:0781165306" className="btn btn--primary">
            <i className="fa-solid fa-phone"></i>
            Appeler maintenant
          </a>
          <Link to="/contact" className="btn btn--outline" onClick={onClose}>
            Envoyer un message
          </Link>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ service, index, onOpen }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      ref={cardRef}
      className={`service-card ${service.highlight ? 'service-card--featured' : ''}`}
      style={{ '--delay': `${index * 0.1}s` }}
      onClick={() => onOpen(service)}
      aria-label={`En savoir plus sur ${service.title}`}
    >
      <div className="service-card__icon">
        <i className={service.icon}></i>
      </div>
      <h3 className="service-card__title">{service.title}</h3>
      <div className="service-card__price">
        <span className="service-card__amount">{service.price}</span>
        {service.priceSuffix && (
          <span className="service-card__suffix">{service.priceSuffix}</span>
        )}
      </div>
      <p className="service-card__details">{service.details}</p>
      <span className="service-card__cta">
        En savoir plus <i className="fa-solid fa-arrow-right"></i>
      </span>
    </button>
  );
}

function Main() {
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    document.title = 'JMD Informatique — Dépannage informatique à domicile · Six-Fours & Var';
    document.querySelector('meta[name="description"]')
      ?.setAttribute('content', 'Dépannage informatique à domicile à Six-Fours-les-Plages et environs. Diagnostic 20 €, dépannage dès 50 €. Déplacements toujours inclus. Soirs et week-ends. ☎ 07 81 16 53 06');
  }, []);

  const handleOpen = useCallback((service) => setActiveService(service), []);
  const handleClose = useCallback(() => setActiveService(null), []);

  return (
    <main className="main" id="main-content">
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__inner">
          <div className="hero__logo animate__animated animate__fadeInDown">
            <img src={logoWhite} alt="JMD Informatique" className="hero__logo-img" />
          </div>
          <h1 className="hero__title animate__animated animate__fadeInUp">
            Dépannage informatique<br />à domicile
          </h1>
          <p className="hero__sub animate__animated animate__fadeIn animate__delay-1s">
            Six-Fours &amp; environs · Déplacements toujours inclus
          </p>
          <div className="hero__actions animate__animated animate__fadeIn animate__delay-1s">
            <a href="tel:0781165306" className="btn btn--primary">
              <i className="fa-solid fa-phone"></i>
              07 81 16 53 06
            </a>
            <Link to="/contact" className="btn btn--outline">
              Envoyer un message
            </Link>
          </div>
        </div>
        <div className="hero__deco" aria-hidden="true" />
      </section>

      {/* ── SERVICES ── */}
      <section className="services section">
        <div className="section__inner">
          <h2 className="section__title">Mes services</h2>
          <p className="section__sub">
            Tarifs clairs, sans surprise. <strong>Les déplacements sont toujours inclus.</strong>
          </p>
          <div className="services__grid">
            {services.map((s, i) => (
              <ServiceCard key={s.title} service={s} index={i} onOpen={handleOpen} />
            ))}
          </div>
        </div>
      </section>

      {/* ── WEB ── */}
      <section className="web-strip section">
        <div className="section__inner">
          <div className="web-strip__card">
            <i className="fa-solid fa-globe web-strip__icon"></i>
            <div>
              <h3 className="web-strip__title">Solutions web</h3>
              <p className="web-strip__text">
                Création de site vitrine, boutique en ligne, blog — disponibles sur demande.
              </p>
            </div>
            <Link to="/contact" className="btn btn--outline web-strip__btn">
              En savoir plus
            </Link>
          </div>
        </div>
      </section>

      {/* ── INFOS ── */}
      <section className="infos section">
        <div className="section__inner">
          <div className="infos__grid">
            <div className="info-block">
              <i className="fa-solid fa-location-dot info-block__icon"></i>
              <h3 className="info-block__title">Zone d'intervention</h3>
              <p>Six-Fours-les-Plages, Sanary-sur-Mer, Bandol et environs.</p>
            </div>
            <div className="info-block">
              <i className="fa-solid fa-clock info-block__icon"></i>
              <h3 className="info-block__title">Disponibilités</h3>
              <p>Soirs à partir de <strong>20h00</strong><br />Samedis &amp; dimanches</p>
            </div>
            <div className="info-block">
              <i className="fa-solid fa-car info-block__icon"></i>
              <h3 className="info-block__title">Déplacements</h3>
              <p>Toujours inclus dans les tarifs. Pas de frais cachés.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta section">
        <div className="section__inner">
          <div className="cta__box">
            <h2 className="cta__title">Un problème informatique ?</h2>
            <p className="cta__text">Appelez directement ou envoyez un message — je reviens vers vous rapidement.</p>
            <a href="tel:0781165306" className="cta__phone">
              <i className="fa-solid fa-phone"></i>
              07 81 16 53 06
            </a>
          </div>
        </div>
      </section>

      {/* ── MODAL ── */}
      {activeService && (
        <ServiceModal service={activeService} onClose={handleClose} />
      )}
    </main>
  );
}

export default Main;
